// Calendrier d'une scène : début et fin ABSOLUS de chaque étape, en temps de
// scène (le temps passé à attendre une réponse de l'élève n'y compte pas).
// Pur et déterministe : le lecteur, la capture vidéo et les tests partent
// tous de ce calendrier.
//
// Règles :
//   - par défaut, une étape démarre quand toutes les étapes précédentes sont
//     finies (curseur = la plus tardive des fins précédentes) ;
//   - `with: X` : démarre en même temps que l'étape précédente X ;
//   - `atSentence: n` : démarre au début de la phrase n de la narration ;
//   - `delayMs` s'ajoute au point de départ ;
//   - une étape interactive est une PORTE : durée nulle dans le calendrier,
//     la scène s'arrête là jusqu'à la réponse de l'élève. Sans ancrage
//     (`with`, `atSentence`), elle s'ouvre quand la voix a FINI : la question
//     ne coupe jamais la narration qui la pose. Une étape écrite
//     APRÈS une porte ne démarre jamais avant elle, même ancrée (`with`,
//     `atSentence`) sur un instant antérieur : sinon elle montrerait la
//     réponse avant la question.
//
// LA VOIX ATTEND L'IMAGE (le temps de voir) :
//   - deux calculs successifs sur la même formule (ou la même valeur) sont
//     espacés d'au moins `CALC_GAP_MS` : on lit un résultat avant le suivant ;
//   - une phrase ne commence pas avant que les étapes de la phrase précédente
//     (sauf une attente `timeline.wait` : rien à lire) soient finies ET
//     restées un temps de lecture à l'écran (`READ_HOLD_MS` :
//     2,5 s après une transformation de formule) : les débuts de phrase sont
//     ÉTIRÉS (`sentenceStartsMs`), la narration aussi (`narrationMs`) ; le
//     lecteur fait attendre la voix jusque-là (plus jamais d'image en
//     rattrapage accéléré pendant un calcul) ;
//   - la scène n'est finie qu'après le temps de lecture de sa dernière étape
//     (`settledMs`).

import type { JrafScene } from './schema/document';
import { DEFAULT_DURATION_MS, isInteractiveStep, stepTargets, type JrafStep } from './schema/steps';
import { estimateSentenceTimings } from './narration';

export interface ScheduledStep {
  id: string;
  index: number;
  action: JrafStep['action'];
  startMs: number;
  endMs: number;
  /** étape interactive : la scène attend l'élève */
  gate: boolean;
  anchor: 'sequence' | 'with' | 'sentence';
  /** instant prévu avant l'espacement des calculs : la phrase dont l'étape relève */
  plannedMs: number;
}

export interface SceneSchedule {
  steps: ScheduledStep[];
  /** fin de la dernière étape */
  timelineEndMs: number;
  /** fin de la dernière étape + son temps de lecture : la scène ne finit pas avant */
  settledMs: number;
  /** fin de scène : étapes (lues) ET narration finies */
  endMs: number;
  /** fin de la narration, pauses de lecture comprises */
  narrationMs: number;
  /** début de chaque phrase, ÉTIRÉ pour laisser voir les étapes de la précédente */
  sentenceStartsMs: number[];
  /** une référence `with` ou `atSentence` n'a pas pu être résolue */
  unresolved: {
    stepId: string;
    reason: 'with_unknown' | 'with_not_before' | 'sentence_out_of_range';
  }[];
  /** étapes écrites après une porte et ancrées avant elle : recalées sur la porte */
  heldByGate: { stepId: string; gateId: string }[];
}

export interface ScheduleOptions {
  /** débuts réels des phrases (voix enregistrée) ; sinon estimés */
  sentenceStartsMs?: readonly number[];
  /** durée réelle de la narration (voix enregistrée) ; sinon estimée */
  narrationMs?: number;
  /** vitesse de la voix du navigateur (estimation) */
  rate?: number;
  /** `false` : débuts de phrase tels quels (sans temps de lecture) */
  stretch?: boolean;
}

/** Décalage des étapes qui suivent une porte (invisible, mais après elle). */
export const AFTER_GATE_MS = 1;

/**
 * Temps de lecture après une étape, avant que la voix continue (ms) : le
 * résultat d'un calcul reste à l'écran avant la phrase suivante.
 */
export const READ_HOLD_MS: Partial<Record<JrafStep['action'], number>> = {
  'formula.transform': 2500,
  'value.set': 1500,
  'label.set': 1000,
  'object.show': 500,
  'object.move': 300,
  'object.scale': 300,
  'object.rotate': 300,
  'object.style': 300,
};

/** Écart minimal entre deux calculs successifs sur la même formule ou la même valeur (ms). */
export const CALC_GAP_MS = 2200;

const CALC_ACTIONS: ReadonlySet<JrafStep['action']> = new Set(['formula.transform', 'value.set']);

/** Temps de lecture d'une étape planifiée. */
export function readHoldMs(step: Pick<ScheduledStep, 'action' | 'gate'>): number {
  return step.gate ? 0 : (READ_HOLD_MS[step.action] ?? 0);
}

export function stepDurationMs(step: JrafStep): number {
  if (isInteractiveStep(step)) return 0;
  if ('durationMs' in step && step.durationMs !== undefined) return step.durationMs;
  return DEFAULT_DURATION_MS[step.action];
}

export function scheduleScene(
  scene: Pick<JrafScene, 'steps' | 'narration'>,
  opts: ScheduleOptions = {},
): SceneSchedule {
  const estimate = estimateSentenceTimings(scene.narration, opts.rate ?? 1);
  const starts = [...(opts.sentenceStartsMs ?? estimate.startsMs)];
  let narrationMs = opts.narrationMs ?? estimate.totalMs;

  let run = placeSteps(scene, starts, narrationMs);
  // Étirement : la phrase k+1 attend que les étapes commencées pendant la
  // phrase k soient finies et lues. Une frontière ne fait que reculer ; on
  // recommence le placement après chaque recul (les ancrages suivent).
  if (opts.stretch !== false) {
    for (let iter = 0; iter < 4 * starts.length + 8; iter++) {
      let moved = false;
      // ce qui suit une question se passe après la réponse : la voix ne l'attend pas
      const firstGate = run.steps.reduce(
        (m, s) => (s.gate ? Math.min(m, s.startMs) : m),
        Number.POSITIVE_INFINITY,
      );
      for (let k = 0; k + 1 < starts.length; k++) {
        const from = starts[k]!;
        const to = starts[k + 1]!;
        let need = 0;
        for (const s of run.steps)
          if (
            !s.gate &&
            s.action !== 'object.highlight' && // « regarde ici » : la voix ne l'attend pas
            s.action !== 'timeline.wait' && // une attente n'a rien à lire (durée de l'intro, du générique)
            s.startMs < firstGate &&
            s.plannedMs >= from &&
            s.plannedMs < to
          )
            need = Math.max(need, s.endMs + readHoldMs(s));
        if (need > to) {
          const d = need - to;
          for (let j = k + 1; j < starts.length; j++) starts[j] = starts[j]! + d;
          narrationMs += d;
          moved = true;
          break;
        }
      }
      if (!moved) break;
      run = placeSteps(scene, starts, narrationMs);
    }
  }

  const timelineEndMs = run.steps.reduce((m, s) => Math.max(m, s.endMs), 0);
  const settledMs = run.steps.reduce((m, s) => Math.max(m, s.endMs + readHoldMs(s)), 0);
  return {
    steps: run.steps,
    timelineEndMs,
    settledMs,
    endMs: Math.max(settledMs, narrationMs),
    narrationMs,
    sentenceStartsMs: starts,
    unresolved: run.unresolved,
    heldByGate: run.heldByGate,
  };
}

/** Place les étapes pour des débuts de phrase donnés. */
function placeSteps(
  scene: Pick<JrafScene, 'steps'>,
  sentenceStarts: readonly number[],
  narrationMs: number,
): Pick<SceneSchedule, 'steps' | 'unresolved' | 'heldByGate'> {
  const out: ScheduledStep[] = [];
  const byId = new Map<string, ScheduledStep>();
  const unresolved: SceneSchedule['unresolved'] = [];
  const heldByGate: SceneSchedule['heldByGate'] = [];
  /** dernier calcul sur chaque formule / valeur (instant prévu, fin) */
  const lastCalc = new Map<string, { plannedMs: number; endMs: number }>();
  let cursor = 0;
  let gate: ScheduledStep | undefined;

  scene.steps.forEach((step, index) => {
    const delay = step.delayMs ?? 0;
    let start = cursor;
    let anchor: ScheduledStep['anchor'] = 'sequence';
    if (step.with !== undefined) {
      const ref = byId.get(step.with);
      if (ref) {
        start = ref.startMs;
        anchor = 'with';
      } else {
        const later = scene.steps.slice(index + 1).some((s) => s.id === step.with);
        unresolved.push({ stepId: step.id, reason: later ? 'with_not_before' : 'with_unknown' });
      }
    } else if (step.atSentence !== undefined) {
      const at = sentenceStarts[step.atSentence];
      if (at !== undefined) {
        start = at;
        anchor = 'sentence';
      } else {
        unresolved.push({ stepId: step.id, reason: 'sentence_out_of_range' });
      }
    }
    start += delay;
    const plannedMs = start;
    // le temps de lire le calcul PRÉCÉDENT (dans le temps prévu, pas dans
    // l'ordre d'écriture : sinon un calcul ancré plus tôt suivrait un calcul
    // ancré plus tard, et l'étirement ne finirait jamais) sur la même cible
    const calcTargets = CALC_ACTIONS.has(step.action) ? stepTargets(step) : [];
    for (const t of calcTargets) {
      const prev = lastCalc.get(t);
      if (prev !== undefined && prev.plannedMs <= plannedMs)
        start = Math.max(start, prev.endMs + CALC_GAP_MS);
    }
    const isGate = isInteractiveStep(step);
    if (isGate && anchor === 'sequence' && start < narrationMs) start = narrationMs;
    if (gate && start < gate.startMs) {
      heldByGate.push({ stepId: step.id, gateId: gate.id });
      start = gate.startMs;
    }
    // l'horloge s'arrête PILE sur la porte : ce qui la suit démarre juste
    // après, jamais pendant la question (la réponse ne s'affiche pas avant)
    if (gate && start <= gate.startMs) start = gate.startMs + AFTER_GATE_MS;
    const scheduled: ScheduledStep = {
      id: step.id,
      index,
      action: step.action,
      startMs: start,
      endMs: start + stepDurationMs(step),
      gate: isGate,
      anchor,
      plannedMs,
    };
    out.push(scheduled);
    if (!byId.has(step.id)) byId.set(step.id, scheduled);
    for (const t of calcTargets) {
      const prev = lastCalc.get(t);
      if (prev === undefined || prev.plannedMs <= plannedMs)
        lastCalc.set(t, { plannedMs, endMs: scheduled.endMs });
    }
    cursor = Math.max(cursor, scheduled.endMs);
    if (isGate) gate = scheduled; // recalée ci-dessus : jamais avant la porte précédente
  });
  return { steps: out, unresolved, heldByGate };
}
